import {useMemo} from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {useDashboardReport} from '../../../hooks/useDashboardReport';
import {
  MONTH_NAMES_FULL,
  MONTH_NAMES_SHORT,
  parseISOWithoutConversion,
} from '../../../utils/dates';
import {ChartOrMessage} from '../../common';

const CustomTooltip = ({active, payload, label}) => {
  if (active && payload && payload.length) {
    const fullDate = payload[0]?.payload?.fullDate || label;
    const value = payload[0].value;

    return (
      <div className='custom-tooltip'>
        <p className='tooltip-label'>{`${fullDate}: ${value} impairment(s)`}</p>
      </div>
    );
  }

  return null;
};

const ImpairedEmployeesChart = ({uid, enabled}) => {
  // Fetch all-time data (no date range filter)
  const {loading, error, recordingsData} = useDashboardReport({
    uid,
    startDate: null,
    endDate: null,
    enabled,
  });

  const flaggedData = useMemo(() => {
    if (!recordingsData || recordingsData.length === 0) {
      return [];
    }

    const dates = recordingsData.map((record) => {
      const parsed = parseISOWithoutConversion(
        record.checkin_datetime_in_org_zone,
      );
      return parsed ? parsed.toDate().getTime() : 0;
    });
    const startDate = new Date(Math.min(...dates));
    startDate.setDate(1);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);

    const dailyData = {};
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      dailyData[dateStr] = {
        date: dateStr,
        value: 0,
      };
      currentDate.setDate(currentDate.getDate() + 1);
    }

    recordingsData.forEach((recording) => {
      if (recording.result === '1' || recording.result === 1) {
        try {
          const parsed = parseISOWithoutConversion(
            recording.checkin_datetime_in_org_zone,
          );
          if (!parsed) {
            return;
          }

          const year = parsed.year;
          const month = String(parsed.month).padStart(2, '0');
          const day = String(parsed.day).padStart(2, '0');
          const dateStr = `${year}-${month}-${day}`;
          if (dailyData[dateStr]) {
            dailyData[dateStr].value += 1;
          }
        } catch (err) {
          console.error('Error processing recording date:', err);
        }
      }
    });

    const dataArray = Object.values(dailyData).sort(
      (a, b) => new Date(a.date) - new Date(b.date),
    );

    const monthGroups = {};
    dataArray.forEach((item, index) => {
      const [year, month, day] = item.date.split('-').map(Number);
      const itemDate = new Date(year, month - 1, day);
      const monthKey = `${itemDate.getFullYear()}-${itemDate.getMonth()}`;

      if (!monthGroups[monthKey]) {
        monthGroups[monthKey] = [];
      }
      monthGroups[monthKey].push(index);
    });

    let previousMonth = null;
    dataArray.forEach((item, index) => {
      const [year, month, day] = item.date.split('-').map(Number);
      const itemDate = new Date(year, month - 1, day);
      const currentMonth = itemDate.getMonth();
      const currentYear = itemDate.getFullYear();
      const monthKey = `${currentYear}-${currentMonth}`;

      item.isMonthBoundary =
        currentMonth !== previousMonth ||
        (index < dataArray.length - 1 &&
          new Date(dataArray[index + 1].date).getMonth() !== currentMonth);

      const monthIndices = monthGroups[monthKey];
      const centerIndex = monthIndices[Math.floor(monthIndices.length / 2)];

      if (index === centerIndex) {
        item.monthLabel = `${MONTH_NAMES_SHORT[currentMonth]} '${(currentYear % 100).toString().padStart(2, '0')}`;
      } else {
        item.monthLabel = '';
      }

      previousMonth = currentMonth;

      item.fullDate = `${MONTH_NAMES_FULL[currentMonth]} ${itemDate.getDate()}, ${currentYear}`;
    });

    return dataArray;
  }, [recordingsData]);

  if (loading) {
    return (
      <div className='chart-container'>
        <h3>IMPAIRED EMPLOYEES</h3>
        <div className='chart'>
          <div className='loading'>Loading chart data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='chart-container'>
        <h3>IMPAIRED EMPLOYEES</h3>
        <div className='chart'>
          <div className='error'>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className='chart-container'>
      <h3>IMPAIRED EMPLOYEES</h3>
      <div className='chart'>
        <ChartOrMessage
          message='No employees have been found impaired yet'
          data={flaggedData}
        >
          <ResponsiveContainer width='100%' height={200}>
            <LineChart data={flaggedData}>
              <CartesianGrid vertical={false} strokeDasharray='3 3' />
              <XAxis
                dataKey='monthLabel'
                tick={{fontSize: 12}}
                interval={0}
                tickLine={false}
              />
              <YAxis allowDecimals={false} domain={[0, 'dataMax']} />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type='monotone'
                dataKey='value'
                stroke='#ff0000'
                activeDot={{r: 8}}
                dot={false}
                name='Impaired Employees'
                connectNulls={true}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartOrMessage>
      </div>
    </div>
  );
};

export default ImpairedEmployeesChart;
